import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { QUEUES, APP_DESTINATIONS, PRESENCE_HEARTBEAT_INTERVAL_MS } from '../constants/webSocketConstants';

/**
 * Global Presence Context for managing all user presence data
 * 
 * This provider:
 * - Uses polling-based presence checking (every 30 seconds)
 * - Tracks users that components are interested in
 * - Provides presence data to all components
 * - Prevents duplicate polling requests
 * - Optimizes WebSocket usage
 */

const PresenceContext = createContext(null);

export const usePresence = () => {
  const context = useContext(PresenceContext);
  if (!context) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
};

export const PresenceProvider = ({ children }) => {
  const { isConnected, subscribe, send, user } = useWebSocket();
  const userId = user?.id;

  // State
  const [userPresence, setUserPresence] = useState(new Map());
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [lastUpdated, setLastUpdated] = useState(null);

  // Refs for stable references
  const subscriptionsRef = useRef(new Set());
  const trackedUsersRef = useRef(new Set()); // Users that components are interested in

  // Keep this user online while connected: the server drops presence that has not been refreshed for 5 minutes
  useEffect(() => {
    if (!isConnected || !send || !userId) {
      return undefined;
    }
    const beat = () => send(APP_DESTINATIONS.PRESENCE_HEARTBEAT, {});
    beat();
    const timer = setInterval(beat, PRESENCE_HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isConnected, send, userId]);

  /**
   * Handle batch presence response from backend
   */
  const handleBatchPresenceResponse = useCallback((message) => {
    if (!message) {
      return;
    }
    
    // Accept both batch responses and real-time updates
    if (message.type !== 'batch' && message.type !== 'PRESENCE_UPDATE') {
      console.warn('⚠️ Unknown presence message type:', message.type);
      return;
    }

    const presenceMap = message.presence;
    if (!presenceMap) return;

    // Update presence state based on response
    const now = new Date();
    setUserPresence(prev => {
      const updated = new Map(prev);
      
      Object.entries(presenceMap || {}).forEach(([userId, isOnline]) => {
        updated.set(userId, {
          userId,
          status: isOnline ? 'online' : 'offline',
          lastSeen: isOnline ? now : updated.get(userId)?.lastSeen || now,
          lastChecked: now
        });
      });
      
      return updated;
    });

    // Update online users set
    setOnlineUsers(prev => {
      const updated = new Set(prev); // Start with existing online users
      Object.entries(presenceMap || {}).forEach(([userId, isOnline]) => {
        if (isOnline) {
          updated.add(userId);
        } else {
          updated.delete(userId); // Remove if now offline
        }
      });
      return updated;
    });

    setLastUpdated(now);
  }, []);

  /**
   * Subscribe to presence updates for tracked users (pub/sub model)
   */
  const subscribeToPresence = useCallback(() => {
    if (!isConnected || !send || !user) {
      console.warn('⚠️ Cannot subscribe to presence:', { isConnected, hasSend: !!send, hasUser: !!user });
      return;
    }

    const usersToTrack = Array.from(trackedUsersRef.current);
    
    if (usersToTrack.length === 0) {
      console.warn('⚠️ No users to subscribe to');
      return;
    }

    const request = {
      type: 'PRESENCE_SUBSCRIBE',
      userIds: usersToTrack,
      requestId: Date.now(),
      timestamp: new Date().toISOString()
    };
    
    send(APP_DESTINATIONS.PRESENCE_SUBSCRIBE, request);
  }, [isConnected, send, user]);

  /**
   * Set up subscription-based presence system (no polling)
   */
  useEffect(() => {   
    if (!isConnected || !user) {
      return;
    }

    // Subscribe to presence updates using Spring's user destination
    // Spring automatically routes to /user/{sessionPrincipal}/queue/presence
    const userDestination = QUEUES.PRESENCE;
    
    const presenceSubscription = subscribe(
      userDestination,
      (message) => {
        handleBatchPresenceResponse(message);
      },
      { headers: { 'user-id': user.id } }
    );

    // Store subscriptions for cleanup
    const currentSubscriptions = subscriptionsRef.current;
    currentSubscriptions.add(presenceSubscription);

    // Mark current user as online immediately
    setUserPresence(prev => new Map(prev).set(user.id, {
      userId: user.id,
      status: 'online',
      lastSeen: new Date().toISOString()
    }));
    setOnlineUsers(prev => new Set(prev).add(user.id));

    // Subscribe to presence updates after delay to let components mount
    const initializeSubscription = () => {
      setTimeout(() => {
        subscribeToPresence();
      }, 2000);
    };

    setTimeout(initializeSubscription, 1000);

    // Cleanup
    return () => {
      currentSubscriptions.forEach(sub => sub.unsubscribe());
      currentSubscriptions.clear();
    };
  }, [isConnected, user, subscribe, handleBatchPresenceResponse, subscribeToPresence]);

  /**
   * Add user tracking method - components call this to register interest in a user
   */
  const trackUser = useCallback((userId) => {
    // Validate UUID format before tracking
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!userId || typeof userId !== 'string' || !uuidRegex.test(userId)) {
      return; // Skip invalid user IDs
    }
    
    if (userId !== user?.id && !trackedUsersRef.current.has(userId)) {
      trackedUsersRef.current.add(userId);
      
      // Subscribe to this user's presence immediately
      if (isConnected && send) {
        const request = {
          type: 'PRESENCE_SUBSCRIBE',
          userIds: [userId],
          requestId: Date.now(),
          timestamp: new Date().toISOString()
        };
        send(APP_DESTINATIONS.PRESENCE_SUBSCRIBE, request);
      }
    }
  }, [user, isConnected, send]);

  const untrackUser = useCallback((userId) => {
    if (trackedUsersRef.current.has(userId)) {
      trackedUsersRef.current.delete(userId);
      
      // Unsubscribe from backend
      if (isConnected && send) {
        const request = {
          type: 'PRESENCE_UNSUBSCRIBE',
          userIds: [userId]
        };
        send(APP_DESTINATIONS.PRESENCE_UNSUBSCRIBE, request);
      }
    }
  }, [isConnected, send]);

  /**
   * Get presence data for a specific user
   */
  const getUserPresence = useCallback((userId) => {
    // Auto-track users that are being queried
    trackUser(userId);
    
    return userPresence.get(userId) || {
      userId,
      status: 'unknown',
      lastSeen: null
    };
  }, [userPresence, trackUser]);

  /**
   * Check if a user is online
   */
  const isUserOnline = useCallback((userId) => {
    // Auto-track users that are being queried (but only if not already tracked)
    trackUser(userId);
    
    const presence = userPresence.get(userId);
    return presence?.status === 'online' || presence?.status === 'away' || presence?.status === 'busy';
  }, [userPresence, trackUser]);

  /**
   * Get all online users
   */
  const getOnlineUsers = useCallback(() => {
    return Array.from(onlineUsers);
  }, [onlineUsers]);

  /**
   * Manual refresh presence data
   */
  const refreshPresence = useCallback(() => {
    subscribeToPresence();
  }, [subscribeToPresence]);

  // Context value
  const contextValue = {
    // Presence data
    userPresence: Object.fromEntries(userPresence), // Convert Map to object for easier use
    userPresenceMap: userPresence, // Raw Map for reactive updates
    onlineUsers: getOnlineUsers(),
    lastUpdated,

    // Connection status
    isConnected,

    // User-specific helpers
    getUserPresence,
    isUserOnline,
    getOnlineUsers,

    // Utility methods
    refreshPresence,
    trackUser,
    untrackUser,

    // For debugging
    trackedUsersCount: trackedUsersRef.current.size
  };

  return (
    <PresenceContext.Provider value={contextValue}>
      {children}
    </PresenceContext.Provider>
  );
};

export default PresenceProvider;
